import { useGetProviderPreferenceTemplateApi } from '@/hooks/api/useGetProviderPreferenceTemplate.tsx';
import { useGetProviderPreferenceDataApi } from '@/hooks/api/useGetProviderPreferenceData.tsx';
import { Button, Loader } from '@ax/ui';
import { useEffect, useRef, useState } from 'react';
import NotePreferenceCheckbox from '@/modules/NotePreferences/NotePreferenceCheckbox.tsx';
import NotePreferencesToggle from '@/modules/NotePreferences/NotePreferencesToggle.tsx';
import { useUpdateProviderPreferenceTemplateApi } from '@/hooks/api/useUpdateProviderPreferenceTemplate.tsx';
import style from './NotePreferences.module.scss';
import toast from 'react-hot-toast';
import useAnalytics from '@/hooks/useAnalytics.tsx';
import InputTypeText from '@/modules/NotePreferences/InputTypeText.tsx';

enum PreferenceControlType {
  CHECKBOX = 'checkbox',
  TOGGLE = 'toggle',
  TEXTAREA = 'textarea',
}

interface NotePreference {
  id: number;
  title: string;
  name: string;
  type: PreferenceControlType;
  subGroupName: string;
  order: number;
  options: any[];
  isUiManaged: boolean;
  uiAttribute?: any;
  hint: {
    title: string;
    content: { title: string; value: string }[];
  };
}

interface GroupedPreferences {
  [key: string]: NotePreference[];
}

const NotePreferences = () => {
  const [formValues, setFormValues]: any = useState({});
  const [configIdMap, setConfigIdMap]: any = useState({});
  const [updateBtnEnabled, setUpdateBtnEnabled] = useState<boolean>(false);
  const [notePreferenceGroups, setNotePreferenceGroups] = useState<GroupedPreferences>({});
  const [disableGroupChildren, setDisableGroupChildren] = useState<string | null>(null);
  const [mixpanelPropertiesState, setMixpanelPropertiesState] = useState<{
    [key: string]: { noteSection: string; action: string };
  }>({});
  const analytics = useAnalytics();

  const { data: preferencesTemplate, isFetching: isFetchingTemplate }: any =
    useGetProviderPreferenceTemplateApi();
  const { data: preferencesTemplateData, isFetching: isFetchingData }: any =
    useGetProviderPreferenceDataApi();
  const { mutate: updateProviderPreferences } = useUpdateProviderPreferenceTemplateApi();
  const prevFormValuesRef = useRef();
  const initialValue = useRef();

  const dispatchMixpanelEvents = () => {
    for (const changes of Object.values(mixpanelPropertiesState)) {
      analytics.onPreferencesSaved(changes);
    }
  };

  const generateTextAreaEventName = (preferenceName: string, value: string) => {
    const preferenceInitialValue =
      initialValue.current && initialValue.current[preferenceName]
        ? initialValue.current[preferenceName]
        : '';

    if (preferenceInitialValue.length > 0 && value.length === 0) {
      return 'Custom instructions removed';
    }

    if (
      preferenceInitialValue.trim().replaceAll(' ', '').length > 0 &&
      value.trim() !== preferenceInitialValue.trim()
    ) {
      return 'Custom instructions updated';
    }

    if (
      preferenceInitialValue.trim().replaceAll(' ', '').length === 0 &&
      value.trim().replaceAll(' ', '').length > 0
    ) {
      return 'New custom instructions added';
    }

    return null;
  };

  const renderContent = () => {
    if (notePreferenceGroups) {
      return (
        <div className={style.preferences__form__container}>
          {Object.keys(notePreferenceGroups).map((groupName) => {
            const groupedUI = notePreferenceGroups[groupName].map(
              (preferenceItem: NotePreference, idx: number) => {
                if (preferenceItem.type === PreferenceControlType.CHECKBOX) {
                  return (
                    <NotePreferenceCheckbox
                      key={preferenceItem.id}
                      title={preferenceItem.title}
                      disabled={
                        disableGroupChildren === groupName ||
                        (idx !== 0 &&
                          formValues[notePreferenceGroups[groupName][0]?.name] === false)
                      }
                      hint={preferenceItem.hint}
                      analyticsProperties={{
                        preferenceType: preferenceItem.title,
                        noteSection: preferenceItem.subGroupName,
                      }}
                      isHeading={idx === 0}
                      attributeName={preferenceItem.name}
                      value={formValues[preferenceItem.name]}
                      onValueChanged={(name: string, value: boolean) => {
                        setUpdateBtnEnabled(true);
                        prevFormValuesRef.current = formValues;

                        if (idx === 0 && !value) {
                          setDisableGroupChildren(groupName);
                        } else {
                          setDisableGroupChildren(null);
                        }

                        setMixpanelPropertiesState({
                          ...mixpanelPropertiesState,
                          [`${name}`]: {
                            action: value ? 'Toggle ON' : 'Toggle OFF',
                            noteSection: preferenceItem.subGroupName,
                          },
                        });

                        setFormValues({
                          ...formValues,
                          [name]: value,
                        });
                      }}
                    />
                  );
                }

                if (preferenceItem.type === PreferenceControlType.TEXTAREA) {
                  return (
                    <InputTypeText
                      key={preferenceItem.id}
                      maxLength={preferenceItem?.uiAttribute?.maxLength}
                      value={formValues[preferenceItem.name] ? formValues[preferenceItem.name] : ''}
                      label={preferenceItem.title}
                      hint={preferenceItem.hint}
                      analyticsProperties={{
                        preferenceType: preferenceItem.title,
                        noteSection: preferenceItem.subGroupName,
                      }}
                      onValueChanged={(value: string) => {
                        setUpdateBtnEnabled(true);
                        prevFormValuesRef.current = formValues;

                        const action = generateTextAreaEventName(preferenceItem.name, value);
                        if (action) {
                          setMixpanelPropertiesState({
                            ...mixpanelPropertiesState,
                            [`${preferenceItem.name}`]: {
                              action,
                              noteSection: preferenceItem.subGroupName,
                            },
                          });
                        }

                        setFormValues({
                          ...formValues,
                          [preferenceItem.name]: value,
                        });
                      }}
                    />
                  );
                }

                if (preferenceItem.type === PreferenceControlType.TOGGLE) {
                  return (
                    <NotePreferencesToggle
                      key={preferenceItem.id}
                      title={preferenceItem.title}
                      attributeName={preferenceItem.name}
                      options={preferenceItem.options}
                      disabled={
                        disableGroupChildren === groupName ||
                        (idx !== 0 &&
                          formValues[notePreferenceGroups[groupName][0]?.name] === false)
                      }
                      hint={preferenceItem.hint}
                      analyticsProperties={{
                        preferenceType: preferenceItem.title,
                        noteSection: preferenceItem.subGroupName,
                      }}
                      value={formValues[preferenceItem.name]}
                      onValueChanged={(name: string, value: string) => {
                        setUpdateBtnEnabled(true);
                        prevFormValuesRef.current = formValues;

                        setMixpanelPropertiesState({
                          ...mixpanelPropertiesState,
                          [`${name}`]: {
                            action: `${preferenceItem.title} changed to ${value}`,
                            noteSection: preferenceItem.subGroupName,
                          },
                        });

                        setFormValues({
                          ...formValues,
                          [name]: value,
                        });
                      }}
                    />
                  );
                }
              },
            );
            return (
              <div
                key={groupName}
                className={style.preferencesGroup__container}
              >
                {groupedUI}
              </div>
            );
          })}

          <div className={style.preferences__update__button}>
            <Button
              className="h-[40px] mt-[10px]"
              variant="primary"
              block
              type="submit"
              onClick={() => {
                const providerPreferenceData = Object.keys(formValues).map(
                  (preferenceName: any) => {
                    return {
                      configId: configIdMap[preferenceName],
                      value: formValues[preferenceName],
                    };
                  },
                );
                updateProviderPreferences(providerPreferenceData, {
                  onSuccess: () => {
                    toast.success('Preferences saved successfully');
                    initialValue.current = formValues;
                    setMixpanelPropertiesState({});
                    dispatchMixpanelEvents();
                  },
                  onError: () => {
                    setFormValues(prevFormValuesRef.current);
                    toast.error('An error occurred, please try again');
                  },
                });
                setUpdateBtnEnabled(false);
              }}
              disabled={!updateBtnEnabled}
            >
              Update
            </Button>
          </div>
        </div>
      );
    } else {
      return <div>No preferences found</div>;
    }
  };

  useEffect(() => {
    if (!isFetchingData && !isFetchingTemplate) {
      const preferences = preferencesTemplate?.preferences || [];
      const configMap = preferences.reduce((configMapObject: any, preference: NotePreference) => {
        if (!configMapObject[preference.name]) {
          configMapObject[preference.name] = preference.id;
          return configMapObject;
        }
        return configMapObject;
      }, {});

      setConfigIdMap(configMap);
      const notePreferenceGroups = preferences
        .map((preference: NotePreference) => {
          const sectionPreference = preferences.find(
            (p: NotePreference) =>
              preference.subGroupName === p.subGroupName &&
              p.type === PreferenceControlType.CHECKBOX,
          );
          if (sectionPreference && !sectionPreference.isUiManaged) {
            preference.isUiManaged = false;
          }
          return preference;
        })
        .sort((a: NotePreference, b: NotePreference) => a.order - b.order)
        .reduce((groupedPreferences: GroupedPreferences, preference: NotePreference) => {
          if (!groupedPreferences[preference.subGroupName] && preference.isUiManaged) {
            groupedPreferences[preference.subGroupName] = [preference];
            return groupedPreferences;
          }

          if (preference.isUiManaged) {
            groupedPreferences[preference.subGroupName].push(preference);
          }
          return groupedPreferences;
        }, {});

      setNotePreferenceGroups(notePreferenceGroups);

      const preferenceDefaults = preferences.reduce((defaults: any, preference: any) => {
        if (Array.isArray(preference.options) && preference.options.length > 0) {
          defaults[preference.name] = preference.options.find((option: any) => option.isDefault)
            ?.value;

          if (defaults[preference.name]) {
            return defaults;
          }
        }

        defaults[preference.name] = preference.defaultValue;
        return defaults;
      }, {});

      setFormValues({
        ...preferenceDefaults,
        ...preferencesTemplateData?.preferences,
      });
      initialValue.current = {
        ...preferenceDefaults,
        ...preferencesTemplateData?.preferences,
      };
    }
  }, [preferencesTemplateData, preferencesTemplate, isFetchingData]);

  if (isFetchingTemplate || isFetchingData) {
    return <Loader color="#344bc6" />;
  }

  if (Object.keys(notePreferenceGroups).length === 0) {
    return (
      <p className={style.content__placeholder}>
        <span className={style.content__placeholder__title}>Preferences</span>
        <span className={style.content__placeholder__text}>
          No preferences options have been set up for your account.
        </span>
      </p>
    );
  }

  return <div className={style.preferences__container}>{renderContent()}</div>;
};

export default NotePreferences;
